import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BookOpen, Eye, EyeOff, FileText, GraduationCap, GripVertical, Layers, Loader2,
  Pencil, Plus, Tags, Trash2, Upload, Users, Video, X,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import ImageUpload from "@/components/admin/ImageUpload";
import LinkInserter from "@/components/admin/LinkInserter";
import ImageInserter from "@/components/admin/ImageInserter";
import ButtonInserter from "@/components/admin/ButtonInserter";
import {
  AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, Td, Th, TableWrap,
  inputCls, textareaCls, useBulkDelete,
} from "@/pages/admin/ui";

const LEVELS = ["Débutant", "Intermédiaire", "Avancé", "Tous niveaux"];
const ACCESS = [
  { value: "permanent", label: "Accès permanent (à vie)" },
  { value: "30", label: "30 jours" }, { value: "90", label: "90 jours" },
  { value: "180", label: "180 jours" }, { value: "365", label: "1 an" },
];
const STATUS_TONE = { published: "green", draft: "amber", unpublished: "slate" };
const STATUS_LABEL = { published: "Publiée", draft: "Brouillon", unpublished: "Non publiée" };

const emptyForm = {
  title: "", short_description: "", description: "", category: "", level: "Débutant", language: "Français",
  price: 0, old_price: 0, duration: "", image_url: "", logo_url: "", gallery: [],
  promo_video_source: "link", promo_video_url: "", instructor_name: "ISMO-TECH", instructor_bio: "",
  objectives: [], prerequisites: [], audience: [], certificate_enabled: true,
  access_duration: "permanent", status: "draft", is_featured: false,
};

const lines = (arr) => (arr || []).join("\n");
const toArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Toggle({ checked, onChange, label, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors ${checked ? "border-brand bg-brand-light text-brand" : "border-line text-slate-500"}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded ${checked ? "bg-brand text-white" : "border border-slate-300"}`}>{checked && "✓"}</span>
      {label}
    </button>
  );
}

// ------------------------------------------------------------------ formation form
function FormationForm({ open, onClose, editing, categories, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (open) setForm(editing ? { ...emptyForm, ...editing } : emptyForm);
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadGallery = async (file) => {
    if (!file) return;
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("gallery", [...(form.gallery || []), data.url]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post(`/admin/formations/upload?formation_id=${editing?.id || ""}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      set("promo_video_url", data.url); set("promo_video_source", "upload");
      toast.success("Vidéo téléversée");
    } catch (e) { toast.error(apiError(e)); } finally { setUploadingVideo(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form, price: Number(form.price) || 0, old_price: Number(form.old_price) || 0,
      is_promo: Number(form.old_price) > Number(form.price),
    };
    try {
      if (editing) await api.put(`/admin/formations/${editing.id}`, payload);
      else await api.post("/admin/formations", payload);
      toast.success(editing ? "Formation mise à jour" : "Formation créée");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={editing ? "Modifier la formation" : "Ajouter une formation"} wide>
      <div className="space-y-4">
        <Field label="Nom de la formation">
          <input data-testid="formation-form-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex : Photoshop Professionnel" />
        </Field>
        <Field label="Description courte" hint="Insérez liens et boutons cliquables">
          <input className={inputCls} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} placeholder="Une phrase d'accroche" />
          <div className="flex flex-wrap gap-4">
            <LinkInserter testId="fm-short" onInsert={(t) => set("short_description", `${form.short_description ? `${form.short_description} ` : ""}${t}`)} />
            <ButtonInserter testId="fm-short" onInsert={(t) => set("short_description", `${form.short_description ? `${form.short_description} ` : ""}${t}`)} />
          </div>
        </Field>
        <Field label="Description complète" hint="Liens, images et boutons s'affichent sur la fiche formation">
          <textarea className={textareaCls} rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} />
          <div className="flex flex-wrap gap-4">
            <LinkInserter testId="fm-desc" onInsert={(t) => set("description", `${form.description ? `${form.description}\n` : ""}${t}`)} />
            <ImageInserter testId="fm-desc" onInsert={(t) => set("description", `${form.description ? `${form.description}\n` : ""}${t}`)} />
            <ButtonInserter testId="fm-desc" onInsert={(t) => set("description", `${form.description ? `${form.description}\n` : ""}${t}`)} />
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Catégorie">
            <select data-testid="formation-form-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">— Choisir —</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Niveau">
            <select className={inputCls} value={form.level} onChange={(e) => set("level", e.target.value)}>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Langue">
            <input className={inputCls} value={form.language} onChange={(e) => set("language", e.target.value)} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Prix (FCFA)"><input data-testid="formation-form-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} /></Field>
          <Field label="Ancien prix (FCFA)"><input type="number" className={inputCls} value={form.old_price} onChange={(e) => set("old_price", e.target.value)} /></Field>
          <Field label="Durée totale"><input className={inputCls} value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="Ex : 8h 30m" /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <ImageUpload label="Image principale" value={form.image_url} onChange={(v) => set("image_url", v)} testId="formation-image" />
          <ImageUpload label="Logo / icône" value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="formation-logo" />
        </div>

        <Field label="Galerie de photos">
          <div className="flex flex-wrap gap-2">
            {(form.gallery || []).map((g, i) => (
              <div key={i} className="relative h-16 w-24 overflow-hidden rounded-lg border border-line">
                <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-slate-500"><X className="h-3 w-3" /></button>
              </div>
            ))}
            <button type="button" onClick={() => galleryRef.current?.click()} className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-line text-slate-400 hover:border-brand hover:text-brand"><Plus className="h-5 w-5" /></button>
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadGallery(e.target.files?.[0])} />
          </div>
        </Field>

        <Field label="Vidéo de présentation">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <select className={`${inputCls} w-36`} value={form.promo_video_source} onChange={(e) => set("promo_video_source", e.target.value)}>
                <option value="link">Lien</option><option value="upload">Fichier</option>
              </select>
              <input className={inputCls} value={form.promo_video_url} onChange={(e) => set("promo_video_url", e.target.value)} placeholder="URL YouTube / Vimeo / MP4" />
              <button type="button" onClick={() => videoRef.current?.click()} disabled={uploadingVideo} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 text-xs font-bold text-ink hover:border-brand">
                {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </button>
              <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
            </div>
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Objectifs (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.objectives)} onChange={(e) => set("objectives", toArr(e.target.value))} /></Field>
          <Field label="Prérequis (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.prerequisites)} onChange={(e) => set("prerequisites", toArr(e.target.value))} /></Field>
          <Field label="Public cible (un par ligne)"><textarea className={textareaCls} rows={4} value={lines(form.audience)} onChange={(e) => set("audience", toArr(e.target.value))} /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Formateur"><input className={inputCls} value={form.instructor_name} onChange={(e) => set("instructor_name", e.target.value)} /></Field>
          <Field label="Bio formateur"><input className={inputCls} value={form.instructor_bio} onChange={(e) => set("instructor_bio", e.target.value)} /></Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Durée d'accès">
            <select className={inputCls} value={form.access_duration} onChange={(e) => set("access_duration", e.target.value)}>
              {ACCESS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
          <Field label="Statut">
            <select data-testid="formation-form-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="draft">Brouillon</option><option value="published">Publiée</option><option value="unpublished">Non publiée</option>
            </select>
          </Field>
        </div>

        <div className="flex flex-wrap gap-2">
          <Toggle checked={form.certificate_enabled} onChange={(v) => set("certificate_enabled", v)} label="Certificat de formation" testId="formation-cert-toggle" />
          <Toggle checked={form.is_featured} onChange={(v) => set("is_featured", v)} label="Mise en avant" testId="formation-featured-toggle" />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="formation-form-save" className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Créer la formation"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

// ------------------------------------------------------------------ lesson editor
const emptyLesson = {
  title: "", description: "", duration: "", video_source: "none", video_url: "", video_file_id: "",
  text_content: "", images: [], files: [], links: [], notes: "",
  is_free_preview: false, is_mandatory: true, is_published: true,
};

function LessonModal({ open, onClose, formationId, moduleId, editing, onSaved }) {
  const [l, setL] = useState(emptyLesson);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const vidRef = useRef(null);

  useEffect(() => { if (open) setL(editing ? { ...emptyLesson, ...editing } : emptyLesson); }, [open, editing]);
  const set = (k, v) => setL((s) => ({ ...s, [k]: v }));

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    const body = new FormData(); body.append("file", file);
    try {
      const { data } = await api.post(`/admin/formations/upload?formation_id=${formationId}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "video") { set("video_source", "upload"); set("video_url", data.url); set("video_file_id", data.file_id); }
      else set("files", [...(l.files || []), data]);
      toast.success("Fichier téléversé");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!l.title.trim()) { toast.error("Titre obligatoire"); return; }
    setSaving(true);
    const payload = { ...l, formation_id: formationId, module_id: moduleId };
    try {
      if (editing) await api.put(`/admin/formation-lessons/${editing.id}`, payload);
      else await api.post("/admin/formation-lessons", payload);
      toast.success("Cours enregistré"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={editing ? "Modifier le cours" : "Ajouter un cours"} wide>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
          <Field label="Titre du cours"><input data-testid="lesson-title" className={inputCls} value={l.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Durée"><input className={inputCls} value={l.duration} onChange={(e) => set("duration", e.target.value)} placeholder="12:45" /></Field>
        </div>
        <Field label="Description"><input className={inputCls} value={l.description} onChange={(e) => set("description", e.target.value)} /></Field>

        <Field label="🎥 Vidéo">
          <div className="flex gap-2">
            <select className={`${inputCls} w-32`} value={l.video_source} onChange={(e) => set("video_source", e.target.value)}>
              <option value="none">Aucune</option><option value="link">Lien</option><option value="upload">Fichier</option>
            </select>
            {l.video_source !== "none" && (
              <>
                <input className={inputCls} value={l.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="URL YouTube / Vimeo / MP4" />
                <button type="button" onClick={() => vidRef.current?.click()} disabled={busy} className="flex shrink-0 items-center rounded-lg border border-line px-3 hover:border-brand"><Upload className="h-4 w-4" /></button>
                <input ref={vidRef} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "video")} />
              </>
            )}
          </div>
        </Field>

        <Field label="📝 Contenu texte"><textarea className={textareaCls} rows={4} value={l.text_content} onChange={(e) => set("text_content", e.target.value)} /></Field>

        <Field label="📄 Fichiers / ressources">
          <div className="space-y-2">
            {(l.files || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-brand" /><span className="flex-1 truncate">{f.name}</span>
                <button type="button" onClick={() => set("files", l.files.filter((_, j) => j !== i))} className="text-rose-500"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink hover:border-brand">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser un fichier
              </button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], "file")} />
            </div>
          </div>
        </Field>

        <Field label="🔗 Liens externes (label|url par ligne)">
          <textarea className={textareaCls} rows={2} value={(l.links || []).map((x) => `${x.label}|${x.url}`).join("\n")}
            onChange={(e) => set("links", e.target.value.split("\n").map((r) => { const [label, url] = r.split("|"); return { label: (label || "").trim(), url: (url || "").trim() }; }).filter((x) => x.url))} />
        </Field>

        <Field label="💡 Notes importantes"><textarea className={textareaCls} rows={2} value={l.notes} onChange={(e) => set("notes", e.target.value)} /></Field>

        <div className="flex flex-wrap gap-2">
          <Toggle checked={l.is_free_preview} onChange={(v) => set("is_free_preview", v)} label="Aperçu gratuit" testId="lesson-free-toggle" />
          <Toggle checked={l.is_mandatory} onChange={(v) => set("is_mandatory", v)} label="Obligatoire" />
          <Toggle checked={l.is_published} onChange={(v) => set("is_published", v)} label="Publié" />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="lesson-save" className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

// ------------------------------------------------------------------ program builder
function ProgramModal({ open, onClose, formation }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lessonModal, setLessonModal] = useState(null); // {moduleId, editing}
  const [dragItem, setDragItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/formations/${formation.id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && formation) load(); /* eslint-disable-next-line */ }, [open, formation]);

  const addModule = async () => {
    const title = window.prompt("Titre du module", `Module ${(detail?.modules?.length || 0) + 1}`);
    if (!title) return;
    await api.post("/admin/formation-modules", { formation_id: formation.id, title });
    load();
  };
  const editModule = async (m) => {
    const title = window.prompt("Titre du module", m.title);
    if (title == null) return;
    await api.put(`/admin/formation-modules/${m.id}`, { formation_id: formation.id, title, description: m.description || "" });
    load();
  };
  const delModule = async (m) => { if (window.confirm(`Supprimer le module « ${m.title} » et ses cours ?`)) { await api.delete(`/admin/formation-modules/${m.id}`); load(); } };
  const delLesson = async (l) => { if (window.confirm(`Supprimer le cours « ${l.title} » ?`)) { await api.delete(`/admin/formation-lessons/${l.id}`); load(); } };

  const moveModule = async (mods) => {
    setDetail((d) => ({ ...d, modules: mods }));
    await api.post("/admin/formation-modules/reorder", { ids: mods.map((m) => m.id) });
  };
  const moveLesson = async (moduleId, lessons) => {
    setDetail((d) => ({ ...d, modules: d.modules.map((m) => (m.id === moduleId ? { ...m, lessons } : m)) }));
    await api.post("/admin/formation-lessons/reorder", { ids: lessons.map((l) => l.id) });
  };
  const onModuleDrop = (toIdx) => {
    if (!dragItem || dragItem.type !== "module" || dragItem.index === toIdx) return setDragItem(null);
    const mods = [...detail.modules];
    const [m] = mods.splice(dragItem.index, 1);
    mods.splice(toIdx, 0, m);
    moveModule(mods);
    setDragItem(null);
  };
  const onLessonDrop = (moduleId, toIdx) => {
    if (!dragItem || dragItem.type !== "lesson" || dragItem.moduleId !== moduleId || dragItem.index === toIdx) return setDragItem(null);
    const mod = detail.modules.find((m) => m.id === moduleId);
    const ls = [...mod.lessons];
    const [l] = ls.splice(dragItem.index, 1);
    ls.splice(toIdx, 0, l);
    moveLesson(moduleId, ls);
    setDragItem(null);
  };

  return (
    <AdminModal open={open} onOpenChange={onClose} title={`Programme — ${formation?.title || ""}`} wide>
      {loading || !detail ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="space-y-3">
          <button onClick={addModule} data-testid="add-module-btn" className="flex items-center gap-2 rounded-lg bg-ink px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Ajouter un module</button>
          {detail.modules.length === 0 && <p className="rounded-xl border border-dashed border-line py-8 text-center text-sm text-slate-500">Aucun module. Ajoutez-en un pour construire le programme.</p>}
          {detail.modules.map((m, mi) => (
            <div
              key={m.id}
              className={`rounded-xl border border-line transition-opacity ${dragItem?.type === "module" && dragItem.index === mi ? "opacity-40" : ""}`}
              data-testid={`module-${m.id}`}
              onDragOver={(e) => { if (dragItem?.type === "module") e.preventDefault(); }}
              onDrop={() => onModuleDrop(mi)}
            >
              <div
                className="flex items-center gap-2 border-b border-line bg-slate-50 px-3 py-2.5"
                draggable
                onDragStart={(e) => { setDragItem({ type: "module", index: mi }); e.stopPropagation(); }}
                onDragEnd={() => setDragItem(null)}
              >
                <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-400 active:cursor-grabbing" data-testid={`drag-module-${m.id}`} />
                <Layers className="h-4 w-4 text-brand" />
                <p className="flex-1 text-sm font-bold text-ink">{m.title}</p>
                <span className="text-xs text-slate-400">{m.lessons.length} cours</span>
                <button onClick={() => editModule(m)} className="text-slate-400 hover:text-brand"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => delModule(m)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="space-y-1 p-2">
                {m.lessons.map((l, li) => (
                  <div
                    key={l.id}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 ${dragItem?.type === "lesson" && dragItem.moduleId === m.id && dragItem.index === li ? "opacity-40" : ""}`}
                    draggable
                    onDragStart={(e) => { setDragItem({ type: "lesson", moduleId: m.id, index: li }); e.stopPropagation(); }}
                    onDragEnd={() => setDragItem(null)}
                    onDragOver={(e) => { if (dragItem?.type === "lesson" && dragItem.moduleId === m.id) { e.preventDefault(); e.stopPropagation(); } }}
                    onDrop={(e) => { e.stopPropagation(); onLessonDrop(m.id, li); }}
                  >
                    <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-slate-300 active:cursor-grabbing" data-testid={`drag-lesson-${l.id}`} />
                    {l.video_source !== "none" ? <Video className="h-4 w-4 text-slate-400" /> : <FileText className="h-4 w-4 text-slate-400" />}
                    <span className="flex-1 text-sm text-slate-600">{l.title}</span>
                    {l.is_free_preview && <Pill tone="green">Aperçu</Pill>}
                    {!l.is_published && <Pill tone="slate">Masqué</Pill>}
                    <span className="text-xs text-slate-400">{l.duration}</span>
                    <button onClick={() => setLessonModal({ moduleId: m.id, editing: l })} className="text-slate-400 hover:text-brand"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => delLesson(l)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setLessonModal({ moduleId: m.id, editing: null })} data-testid={`add-lesson-${m.id}`} className="flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand"><Plus className="h-3.5 w-3.5" /> Ajouter un cours</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {lessonModal && (
        <LessonModal open onClose={() => setLessonModal(null)} formationId={formation.id} moduleId={lessonModal.moduleId} editing={lessonModal.editing} onSaved={load} />
      )}
    </AdminModal>
  );
}

// ------------------------------------------------------------------ categories modal
function CategoriesModal({ open, onClose }) {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/formation-categories").then(({ data }) => setCats(data));
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => { if (!name.trim()) return; try { await api.post("/admin/formation-categories", { name }); setName(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (c) => { if (window.confirm(`Supprimer « ${c.name} » ?`)) { try { await api.delete(`/admin/formation-categories/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); } } };
  return (
    <AdminModal open={open} onOpenChange={onClose} title="Catégories de formations">
      <div className="space-y-3">
        <div className="flex gap-2">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nouvelle catégorie" data-testid="cat-name-input" />
          <button onClick={add} data-testid="cat-add-btn" className="rounded-lg bg-brand px-4 text-sm font-bold text-white">Ajouter</button>
        </div>
        <div className="divide-y divide-line">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-2 py-2.5">
              <span className="h-3 w-3 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 text-sm font-semibold text-ink">{c.name}</span>
              <span className="text-xs text-slate-400">{c.slug}</span>
              <button onClick={() => del(c)} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </AdminModal>
  );
}

// ------------------------------------------------------------------ main page
export default function AdminFormations() {
  const [formations, setFormations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState(null); // {editing}
  const [programFor, setProgramFor] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);
  const { selected, toggle, toggleAll, bulkDelete } = useBulkDelete("/admin/formations/bulk-delete", () => load(), "formation(s)");

  const load = async () => {
    setLoading(true);
    try {
      const [f, c, s] = await Promise.all([
        api.get("/admin/formations", { params: { q, status: statusFilter } }),
        api.get("/admin/formation-categories"),
        api.get("/admin/formations/stats"),
      ]);
      setFormations(f.data); setCategories(c.data); setStats(s.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, statusFilter]);

  const setStatus = async (f, status) => { try { await api.put(`/admin/formations/${f.id}/status`, { status }); toast.success("Statut mis à jour"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (f) => { if (window.confirm(`Supprimer « ${f.title} » et tout son contenu ?`)) { try { await api.delete(`/admin/formations/${f.id}`); toast.success("Formation supprimée"); load(); } catch (e) { toast.error(apiError(e)); } } };

  const STAT_CARDS = [
    { icon: GraduationCap, tone: "bg-brand-light text-brand", value: stats.total || 0, label: "Formations" },
    { icon: Eye, tone: "bg-emerald-50 text-emerald-600", value: stats.published || 0, label: "Publiées" },
    { icon: Users, tone: "bg-violet-50 text-violet-600", value: stats.students || 0, label: "Étudiants" },
    { icon: BookOpen, tone: "bg-amber-50 text-amber-600", value: stats.certificates || 0, label: "Certificats" },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {STAT_CARDS.map((s) => (
          <div key={s.label} className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4">
            <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.tone}`}><s.icon className="h-5 w-5" /></span>
            <div><p className="font-display text-xl font-extrabold text-ink">{s.value}</p><p className="text-xs text-slate-500">{s.label}</p></div>
          </div>
        ))}
      </div>

      <AdminCard
        title="Formations"
        description="Créez et gérez vos formations en ligne."
        action={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCatsOpen(true)} data-testid="manage-categories-btn" className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink hover:border-brand"><Tags className="h-4 w-4" /> Catégories</button>
            <button onClick={() => setFormModal({ editing: null })} data-testid="add-formation-btn" className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white"><Plus className="h-4 w-4" /> Ajouter une formation</button>
          </div>
        }
      >
        <div className="mb-3 flex flex-wrap gap-2">
          <input className={`${inputCls} max-w-xs`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." data-testid="formation-admin-search" />
          <select className={`${inputCls} w-44`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous les statuts</option><option value="published">Publiées</option><option value="draft">Brouillons</option><option value="unpublished">Non publiées</option>
          </select>
        </div>
        <BulkDeleteBar count={selected.length} onDelete={bulkDelete} testId="bulk-bar-formations" />
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <TableWrap>
            <table className="w-full text-left">
              <thead><tr className="border-b border-line">
                <Th><SelectCheckbox checked={selected.length > 0 && selected.length >= formations.length} onChange={() => toggleAll(formations.map((f) => f.id))} testId="bulk-all-formations" /></Th>
                <Th>Formation</Th><Th>Catégorie</Th><Th>Prix</Th><Th>Contenu</Th><Th>Étudiants</Th><Th>Statut</Th><Th className="text-right">Actions</Th>
              </tr></thead>
              <tbody>
                {formations.map((f) => (
                  <tr key={f.id} className="border-b border-line/70" data-testid={`formation-admin-row-${f.slug}`}>
                    <Td><SelectCheckbox checked={selected.includes(f.id)} onChange={() => toggle(f.id)} testId={`bulk-formation-${f.id}`} /></Td>
                    <Td>
                      <div className="flex items-center gap-2.5">
                        {f.image_url ? (
                          <img src={f.image_url} alt="" className="h-10 w-16 rounded-lg object-cover" />
                        ) : (
                          <span className="flex h-10 w-16 items-center justify-center rounded-lg bg-brand-light text-brand"><GraduationCap className="h-5 w-5" /></span>
                        )}
                        <span className="font-bold text-ink">{f.title}</span>
                      </div>
                    </Td>
                    <Td><span className="text-slate-500">{f.category || "—"}</span></Td>
                    <Td>{formatPrice(f.price)}</Td>
                    <Td><span className="text-xs text-slate-500">{f.modules_count} mod. • {f.lessons_count} cours</span></Td>
                    <Td>{f.students_count}</Td>
                    <Td><Pill tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status]}</Pill></Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setProgramFor(f)} title="Programme" data-testid={`program-btn-${f.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-brand-soft hover:text-brand"><Layers className="h-4 w-4" /></button>
                        <button onClick={() => setFormModal({ editing: f })} title="Modifier" data-testid={`edit-formation-${f.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-brand-soft hover:text-brand"><Pencil className="h-4 w-4" /></button>
                        {f.status === "published" ? (
                          <button onClick={() => setStatus(f, "unpublished")} title="Dépublier" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><EyeOff className="h-4 w-4" /></button>
                        ) : (
                          <button onClick={() => setStatus(f, "published")} title="Publier" data-testid={`publish-${f.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><Eye className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => del(f)} title="Supprimer" data-testid={`delete-formation-${f.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {formations.length === 0 && <p className="py-12 text-center text-sm text-slate-500">Aucune formation. Cliquez sur « Ajouter une formation ».</p>}
          </TableWrap>
        )}
      </AdminCard>

      {formModal && <FormationForm open onClose={() => setFormModal(null)} editing={formModal.editing} categories={categories} onSaved={load} />}
      {programFor && <ProgramModal open onClose={() => setProgramFor(null)} formation={programFor} />}
      <CategoriesModal open={catsOpen} onClose={() => setCatsOpen(false)} />
    </div>
  );
}
